import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Box,
	BoxProps,
	Divider,
	Flex,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tooltip,
	Tr,
	VStack
} from "@chakra-ui/react";
import { MdInfoOutline } from "react-icons/md";
import { DisplayCardStatementProps } from "../../utils/types";

interface DataCardDisplayStatementProps {
	data?: DisplayCardStatementProps;
	style?: BoxProps;
}

const DataCardDisplayStatement = ({
	data,
	style = {} // Default styles here
}: DataCardDisplayStatementProps) => {
	const isFlat = ["N/A", "NaN"].includes(data?.data?.billingItems?.[0]?.hours);
	const hasHighFixedValueServices = data?.data?.hasHighFixedValueServices || false;

	return (
		<Box bg="offWhite" borderRadius="5px" flexShrink="0" {...style}>
			<Flex direction="column" gap="8px" py={2}>
				{/* Main Itemized Table Section */}
				<Table variant="unstyled" size="sm">
					<Thead>
						<Tr>
							<Th color="gray.800" fontSize="16px" fontWeight="600" textTransform="none">
								Service
							</Th>
							<Th color="gray.800" fontSize="16px" fontWeight="600" textTransform="none">
								Recipient
							</Th>
							{!isFlat && (
								<>
									<Th color="gray.800" fontSize="16px" fontWeight="600" textTransform="none">
										Provider Type
									</Th>
									<Th color="gray.800" fontSize="16px" fontWeight="600" textTransform="none">
										Hours
									</Th>
								</>
							)}
							<Th color="gray.800" fontSize="16px" fontWeight="600" textTransform="none">
								{!isFlat && !hasHighFixedValueServices ? "Network Rate" : "Network Maximum"}
							</Th>
							<Th color="gray.800" fontSize="16px" fontWeight="600" textTransform="none">
								{!isFlat && !hasHighFixedValueServices ? "Standard Rate" : "Standard Flat Fee"}
							</Th>
							<Th color="gray.800" fontSize="16px" fontWeight="600" textTransform="none">
								Total
							</Th>
						</Tr>
					</Thead>
					<Tbody>
						{data &&
							data.data.billingItems.map((item, index) => (
								<Tr key={index}>
									<Td color="gray.800" fontSize="16px" fontWeight="400">
										{item.isHighFixedValue ? (
											<Accordion allowToggle>
												<AccordionItem border="0">
													<AccordionButton px={0}>
														<Box as="span" flex="1" textAlign="left">
															{item.service}
														</Box>
														<AccordionIcon />
													</AccordionButton>
													<AccordionPanel px={0}>
														{item.selectedCaseWorkLevels?.length ? (
															<VStack align="stretch" spacing={1}>
																{item.selectedCaseWorkLevels.map((workLevel) => (
																	<Text key={workLevel}>{workLevel}</Text>
																))}
															</VStack>
														) : (
															"No service levels selected"
														)}
													</AccordionPanel>
												</AccordionItem>
											</Accordion>
										) : (
											item.service
										)}
									</Td>
									<Td color="gray.800" fontSize="16px" fontWeight="400">
										{item.recipient}
									</Td>
									{!isFlat && (
										<>
											<Td color="gray.800" fontSize="16px" fontWeight="400">
												{item.providerType}
											</Td>
											<Td color="gray.800" fontSize="16px" fontWeight="400">
												{item.hours}
											</Td>
										</>
									)}
									<Td color="gray.800" fontSize="16px" fontWeight="400">
										{item.isHighFixedValue && item.tooltipData ? (
											<Box display="flex" alignItems="center" gap={2}>
												<Text fontWeight="400" color="gray.800" fontSize="16px">
													${item.networkRate}
												</Text>
												<Tooltip
													label={`The highest level of work done on this case was "${item.tooltipData.highestWorkLevel}". The payout for this level of work is the Network Maximum × Case work percentage = Total. $${item.tooltipData.networkMaximum} × ${item.tooltipData.percentage}% = $${item.tooltipData.calculatedTotal}.`}
													placement="top"
													hasArrow
													bg="gray.700"
													color="white">
													<Box display="inline-flex" alignItems="center">
														<MdInfoOutline style={{ fontSize: "16px", cursor: "pointer" }} />
													</Box>
												</Tooltip>
											</Box>
										) : (
											<Text fontWeight="400" color="gray.800" fontSize="16px">
												${item.networkRate}
											</Text>
										)}
									</Td>
									<Td color="gray.800" fontSize="16px" fontWeight="400">
										${item.standardRate}
									</Td>
									<Td color="gray.800" fontSize="16px" fontWeight="400">
										<Text fontWeight="400" color="gray.800" fontSize="16px">
											${item.totalAmount}
										</Text>
									</Td>
								</Tr>
							))}
					</Tbody>
				</Table>

				{/* Divider Line */}
				<Divider width="auto" borderWidth="1px" borderColor="gray.300" height="0px" flexShrink="0" mx={4} my="0" />

				{/* Summary Section with Two Columns */}
				<Flex justify="space-between">
					{/* Left Column */}
					<VStack align="flex-start" spacing="4px" width="50%" marginRight="56px" marginLeft="20px">
						<Flex justify="space-between" width="100%">
							<Text fontWeight="600" color="gray.800" fontSize="16px">
								Other Non-Covered Charges:
							</Text>
							<Text fontWeight="400" color="gray.800" fontSize="16px">
								${data?.data.summary.otherNonCoveredFees}
							</Text>
						</Flex>
						<Flex justify="space-between" width="100%">
							<Text fontWeight="600" color="gray.800" fontSize="16px">
								Non-Covered Services/Hours Shown on Invoice:
							</Text>
							<Text fontWeight="400" color="gray.800" fontSize="16px">
								${data?.data.summary.otherNonCoveredHours}
							</Text>
						</Flex>
					</VStack>

					{/* Right Column */}
					<VStack align="flex-start" spacing="4px" width="50%" paddingRight="20px">
						{!!data?.data.summary.totalHours && (
							<Flex justify="space-between" width="100%">
								<Text fontWeight="600" color="gray.800" fontSize="16px">
									Total Hours:
								</Text>
								<Text fontWeight="400" color="gray.800" fontSize="16px">
									{data?.data.summary.totalHours}
								</Text>
							</Flex>
						)}
						<Flex justify="space-between" width="100%">
							<Text fontWeight="600" color="gray.800" fontSize="16px">
								Total Standard Cost:
							</Text>
							<Text fontWeight="400" color="gray.800" fontSize="16px">
								${data?.data.summary.totalStandardCost}
							</Text>
						</Flex>
						<Flex justify="space-between" width="100%">
							<Text fontWeight="600" color="gray.800" fontSize="16px">
								Market Value:
							</Text>
							<Text fontWeight="400" color="gray.800" fontSize="16px">
								${data?.data.summary.marketValue}
							</Text>
						</Flex>
						<Flex justify="space-between" width="100%">
							<Text fontWeight="600" color="gray.800" fontSize="16px">
								Total Network Cost:
							</Text>
							<Text fontWeight="400" color="gray.800" fontSize="16px">
								${data?.data.summary.totalNetworkCost}
							</Text>
						</Flex>
					</VStack>
				</Flex>
			</Flex>
		</Box>
	);
};

export default DataCardDisplayStatement;
